import React, { useCallback } from "react";
import { Card, DropdownButton, Text } from "@jsq/ds/core";
import { RedactedPaymentPref } from "../../common/types";
import { getPaymentMethod } from "../../common/utils";
import { useConsolidatedPaymentPrefContext } from "../../Context";
import { ExistingPaymentPrefDisplay } from "../ExistingPaymentPrefDisplay";
import { PAYMENT_INSTRUCTIONS_LABEL } from "../PaymentPrefActions/constants";
import { type ChildComponentProps } from "../types";
import * as styles from "./styles_vanilla.css";
import { type PayeeEditPaymentPrefActionsProps } from "./types";

/**
 * Component that displays existing payment preference details with action buttons
 * for editing instructions or switching payment method.
 *
 * This component is used as the initial step in the payee edit workflow,
 * allowing users to choose between editing the current payment method details
 * or switching to a different payment method entirely.
 *
 * When "Edit instructions" is clicked, it sets the payment method and continues
 * directly to the payment form (skipping SelectPaymentMethod).
 *
 * When "Switch payment method" is clicked, it clears the payment method so that
 * SelectPaymentMethod will be shown next.
 */
export const PayeeEditPaymentPrefActions: React.FC<
  PayeeEditPaymentPrefActionsProps & ChildComponentProps
> = ({ paymentPref, onContinueClick }) => {
  const { setSelectedPaymentMethod, setPaymentPrefDetail } =
    useConsolidatedPaymentPrefContext();
  const paymentMethod = getPaymentMethod(paymentPref);

  const handleEditInstructions = useCallback(() => {
    // Keep the current payment method selected so we go directly to the form
    setSelectedPaymentMethod(paymentMethod);
    // Keep the existing payment pref detail for editing
    setPaymentPrefDetail(paymentPref);
    onContinueClick();
  }, [
    paymentMethod,
    paymentPref,
    setSelectedPaymentMethod,
    setPaymentPrefDetail,
    onContinueClick,
  ]);

  const handleSwitchPaymentMethod = useCallback(() => {
    // Clear the selected payment method so SelectPaymentMethod will be shown
    setSelectedPaymentMethod(undefined);
    // Clear the payment pref detail so a new one will be created
    setPaymentPrefDetail(undefined);
    onContinueClick();
  }, [setSelectedPaymentMethod, setPaymentPrefDetail, onContinueClick]);

  // Transform DetailedPaymentPrefUnionObjectType to RedactedPaymentPref format for display
  // ExistingPaymentPrefDisplay expects RedactedPaymentPref which has id, paymentMethod, and bankInfoRedacted
  // The union type has bankInfoRedacted for ACH/Wire/Check but not NonUs
  const displayPaymentPref: RedactedPaymentPref = {
    id: paymentPref.id ?? "",
    paymentMethod,
    bankInfoRedacted: "bankInfoRedacted" in paymentPref ? paymentPref.bankInfoRedacted : null,
    verificationStatus: null,
    returnCode: null,
    codeReason: null,
    nocCorrections: null,
  };

  const actionsMenu = (
    <DropdownButton
      data-testid="payee-edit-payment-pref-action-trigger"
      variant="muted"
      align="right"
      direction="down"
      icon="ellipsis-h"
    >
      <DropdownButton.Action onSelect={handleEditInstructions}>
        Edit {PAYMENT_INSTRUCTIONS_LABEL[paymentMethod]} instructions
      </DropdownButton.Action>
      <DropdownButton.Action onSelect={handleSwitchPaymentMethod}>
        Switch payment method
      </DropdownButton.Action>
    </DropdownButton>
  );

  return (
    <>
      <Text className={styles.title} variant="h3">
        Payment instructions
      </Text>

      <Card>
        <ExistingPaymentPrefDisplay
          paymentPref={displayPaymentPref}
          displayRevealDetails={true}
          defaultExpanded={false}
          rightAccessory={actionsMenu}
        />
      </Card>
    </>
  );
};
